import type {
  ConnectorMeta,
  ConnectorPermissionLevel,
} from '@research-assistant/shared';
import {
  CheckIcon,
  Loader2Icon,
  SettingsIcon,
  TriangleAlertIcon,
  XIcon,
} from 'lucide-react';
import { useEffect, useRef, useState, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { DialogClose } from '@/components/ui/dialog.js';
import { useHintTarget } from '@/features/onboarding/useHintTarget.js';
import { SelectFoldersDialog } from '@/features/sharepoint/SelectFoldersDialog.js';
import { notify } from '@/lib/notify.js';
import { cn } from '@/lib/utils.js';

import { useInvalidateConnections } from '../../hooks/useConnections.js';
import * as api from '../../services/integration-api.js';
import type {
  ConnectionProfilePatch,
  ConnectorConnection,
  ConnectorConnectionScope,
  ExternalTenantConsent,
} from '../../services/integration-api.js';

import { ConnectorIcon } from './ConnectorIcon.js';
import { SelectFoldersButton } from './SelectFoldersButton.js';
import { ConnectionSettingsDialog } from './ConnectionSettingsDialog.js';
import { accessSummary } from './ConnectorAccessDialog.js';
import { useConnectorConnectFlow } from './ConnectorConnectFlow.js';
import {
  connectionOriginHint,
  connectionOriginPillSuffix,
} from './connection-origin.js';
import { useConnectorScope } from './connector-scope.js';

/**
 * Providers whose connection exposes nothing until somebody picks what it may reach. The card states
 * the count, because a connected-looking connector that answers nothing is the state this whole
 * feature exists to make visible.
 */
const FOLDER_SELECTION_CONNECTORS: ReadonlySet<string> = new Set([
  'sharepoint',
]);

/**
 * Compact neutral button — explicitly avoids the `Button` component's
 * `outline` / `default` variants because both bring brand (accent / primary)
 * color into hover & focus states. Keeps all card actions monochrome.
 */
const cardButtonClass =
  'inline-flex min-h-7 shrink-0 items-center justify-center gap-1 rounded-full py-1 text-left border border-border bg-transparent px-3 text-xs font-medium text-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20 disabled:cursor-not-allowed disabled:opacity-50';

interface ConnectorCardProps {
  meta: ConnectorMeta;
  connected: boolean;
  /**
   * Which lane this card's connection sits in. Disconnect is offered only for `'workspace'` — the
   * caller's own connection created here. `'personal'` is their own too but managed from their personal
   * space, so it is shown connected with no disconnect. `null`/unknown withholds it as well, so an
   * unexpected lane cannot offer a disconnect that 404s.
   */
  connectedVia?: ConnectorConnectionScope;
  /**
   * The level the resolved workspace connection granted, from the same presence entry as
   * `connected`. Deliberately NOT `meta.permission_level`: the catalog reports the caller's own
   * connection, so on a workspace-shared connector it reads `read` for every member who did not
   * connect it. `null`/absent means unknown — no permission claim is made.
   */
  permissionLevel?: ConnectorPermissionLevel | null;
  /**
   * The named connection this card stands for, when it stands for one. A caller reaching a provider
   * through several connections gets one card each, and this is what tells them apart: the label they
   * read, the instruction the agent reads, and the `connection_id` every action takes. Absent on the
   * not-connected card, which has nothing to name.
   *
   * The connected state itself still arrives through {@link connected} / {@link connectedVia} /
   * {@link permissionLevel}: the directory maps a connection to those, so this card has one source of
   * truth for what the connection grants and this field only carries its identity.
   */
  connection?: ConnectorConnection;
  /**
   * Every connection of this provider the CALLER already holds here, in the server's order — including
   * the one this card stands for. It is what decides whether the connect form has to ask for a name and
   * an instruction: with none there is nothing to tell apart, and with one there is (§3). It is also the
   * list the 409 repair fills in, because the refusal is about those connections and not about this one.
   *
   * Only the grid can say this — a card on its own cannot see its neighbours.
   */
  peerConnections?: ConnectorConnection[];
  /**
   * Whether this card carries the provider's "add another connection" action.
   *
   * The grid used to set it on one card per provider, to avoid repeating a button. That read as the
   * first card being special: with two SharePoints on screen only one of them offered it, and a
   * person looking at the other had nowhere to press. Every card of a provider carries it now —
   * a card stands for a connection, and adding a connection is something any of them can start.
   */
  offersAnotherConnection?: boolean;
  /**
   * Whether this deployment can connect a customer's own directory at all (§15), straight off the
   * presence read. It only decides whether the customer option in the tenant choice is selectable —
   * the option is shown either way, with the reason.
   */
  externalTenantLaneAvailable?: boolean;
  /**
   * The customer directories already approved for this workspace, straight off the presence read.
   * Empty means the tenant choice can only hand over the approval link; a non-empty list is what turns
   * the customer option into an ordinary sign-in.
   */
  approvedExternalTenants?: ExternalTenantConsent[];
  /**
   * Start this card's connect as soon as it mounts, once.
   *
   * Connecting is not one click for every provider — SharePoint asks for a name, sometimes whose
   * directory it is, sometimes what it may do — and that whole flow lives here. A surface elsewhere
   * that offers "connect another" therefore sends the person to this card instead of growing a second
   * copy of the flow, and this is how the flow carries on where they left off.
   */
  connectOnOpen?: boolean;
  /** Disable new connection actions without unmounting an active flow. */
  connectDisabled?: boolean;
}

/**
 * Connector card, modeled after Anthropic / Linear / Vercel directory cards:
 *
 *   ┌──────────────────────────────────────────────────┐
 *   │ [icon]  Name                       [✓ Connected] │
 *   │         Vendor                                   │
 *   │                                                  │
 *   │  2-line description, line-clamped                │
 *   └──────────────────────────────────────────────────┘
 *
 * Conventions:
 * - "Not connected" is the default state; no negative-state pill (matches
 *   Anthropic & Linear).
 * - One pill in the top-right carries both state and intent: green
 *   "✓ Connected" by default, morphs to red "✕ Disconnect" on hover.
 * - `min-h-[8.5rem]` enforces equal heights; description is line-clamped to
 *   two lines so long copy never breaks the grid.
 *
 * Scope comes from the surrounding `ConnectorScopeProvider`: connect / disconnect target that workspace.
 * `canManage` no longer hides the connect — everybody connects under their own account and the service
 * gates on membership, so hiding the button from someone the API allows would be the interface lying.
 * It gates nothing else here any more: the api-key service lane it used to guard is gone from the card,
 * because a credential belonging to the workspace rather than to a person cannot be made from here.
 *
 * One card is one CONNECTION, not one provider, wherever there is a connection to name ({@link
 * connection}): a person can hold several connections of a provider here, and those are different
 * places rather than candidates for one slot. Every action the card offers names its `connection_id`, so
 * a disconnect cannot land on the neighbouring connection.
 */
export function ConnectorCard({
  meta,
  connected,
  connectedVia,
  permissionLevel,
  connection,
  peerConnections = [],
  offersAnotherConnection = false,
  externalTenantLaneAvailable = false,
  approvedExternalTenants = [],
  connectOnOpen = false,
  connectDisabled = false,
}: ConnectorCardProps): JSX.Element {
  const { workspaceId, canManage, sharing = 'unknown' } = useConnectorScope();
  /**
   * Whether this connection is only VISITING the workspace on screen — the caller's personal connection,
   * seen from a working workspace it travels into. Such a card says "manage it in your personal space"
   * and offers no actions, which is right there and wrong at home: in the caller's OWN personal space
   * the same lane is the connection that actually lives there, so the card was telling somebody to go
   * manage it somewhere they already were, and offering nothing to do it with.
   */
  const inheritedFromPersonal =
    connected && connectedVia === 'personal' && sharing !== 'personal';
  const [disconnecting, setDisconnecting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [foldersOpen, setFoldersOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const invalidate = useInvalidateConnections();
  const flow = useConnectorConnectFlow({
    meta,
    permissionLevel,
    allowDelete: connection?.allowDelete,
    connectionLabel: connection?.label,
    peerConnections,
    externalTenantLaneAvailable,
    approvedExternalTenants,
  });
  const { oauth, savingAccess, startConnect } = flow;
  /**
   * What a disconnect is about to remove, in the words the user sees. The connection's own label where
   * there is one: with two connections of a provider on screen, "Disconnect SharePoint?" does not say
   * which of them is going.
   */
  const disconnectTarget = connection ? connection.label : meta.name;
  /**
   * What the status pill says after "Connected", when this connection lives somewhere other than the
   * workspace on screen. Only the travelling lane is marked, and only in a shared workspace — the rule
   * and its reasons sit with the labels in `connection-origin`.
   *
   * It rides IN the pill rather than beside it: the pill is where a person looks for the state of a
   * connector, and the one thing that qualifies that state belongs in the same breath. Its second job
   * on hover ("Disconnect") does not collide, because a card that shows the suffix is a card whose
   * connection is managed elsewhere and therefore offers no disconnect at all.
   */
  const originSuffix = connectionOriginPillSuffix(
    connectedVia ?? null,
    sharing,
  );
  /** What the suffix means, as the pill's own title — the only place with room for the sentences. */
  const originHint = connectionOriginHint(connectedVia ?? null, sharing);
  /**
   * Whether this card may take its connection away.
   *
   * `'workspace'` is the caller's own connection created here, so yes. `'personal'` is also theirs, and
   * the rule used to stop there — correctly, for a WORKING workspace: a personal connection is only
   * inherited into it, and removing it from a place it does not live would be a surprise. But in the
   * caller's OWN personal space that same connection is exactly the one they came to remove, and the
   * service allows it — a `DELETE` there answers 200 and the row is gone. Withholding the control left
   * a connection nobody could disconnect from anywhere.
   *
   * An unknown lane still withholds it, so a value this build does not recognise cannot offer a
   * disconnect that 404s.
   */
  const canDisconnect =
    connectedVia === 'workspace' ||
    (connectedVia === 'personal' && sharing === 'personal');
  /**
   * Whether to offer a SECOND connection of this provider. Never in the caller's personal space: §2
   * gives a person exactly one personal connection per provider, and the service upserts there — a
   * second connect returns the same connection id and leaves one row. Offering it would promise
   * something the service does not do.
   */
  const offersAnother = offersAnotherConnection && sharing !== 'personal';
  /**
   * Whether this card offers the folder list.
   *
   * This is the ONE control a visiting personal connection still gets. Everything else such a card
   * hides — the name, the instruction, disconnecting — belongs to the connection and is managed where
   * it lives. What a connection may reach does not: places and folders are keyed by connection AND
   * workspace, so a personal connection exposes a different set in every workspace it travels into,
   * and each of those sets can only be chosen from the workspace it belongs to. Hiding the button
   * here left the visiting card with no way to answer the question it exists to raise.
   */
  const selectsFolders = connected && FOLDER_SELECTION_CONNECTORS.has(meta.id);
  /**
   * Opens the folder list the moment a connection appears on THIS card — which is what a just
   * finished connect looks like from here. A connection that reaches nothing is the state the
   * person is now in, and leaving them on a card that says "Connected" hides it behind a click
   * they have no reason to make yet.
   *
   * A transition, never a state: a card that mounts already connected opens nothing, so revisiting
   * the page does not reopen a dialog somebody closed.
   */
  const wasConnected = useRef(connected);
  useEffect(() => {
    if (
      !wasConnected.current &&
      connected &&
      FOLDER_SELECTION_CONNECTORS.has(meta.id)
    ) {
      setFoldersOpen(true);
    }
    wasConnected.current = connected;
  }, [connected, meta.id]);
  /**
   * Identity knows this connection's credential is dead (an expired token, or a refresh the provider
   * refused), so it is not really connected until somebody reconnects it.
   */
  const needsReconnect = connected && connection?.needsReconnect === true;
  /** The state the pill shows, in words, colour and icon. */
  const pillLabel = needsReconnect
    ? 'Needs reconnect'
    : originSuffix
      ? `Connected ${originSuffix}`
      : 'Connected';
  const pillTone = needsReconnect
    ? 'bg-tone-warning/12 text-tone-warning'
    : 'bg-tone-success/12 text-tone-success';
  const PillIcon = needsReconnect ? TriangleAlertIcon : CheckIcon;
  /**
   * What the card says about the access of a live connection. An unknown level says nothing, since a
   * guess here is a claim about access, and an unknown Allow delete claims no delete.
   */
  const accessLine =
    connected && !needsReconnect && permissionLevel
      ? accessSummary(meta.id, {
          permissionLevel,
          allowDelete: connection?.allowDelete === true,
        })
      : null;
  /**
   * Whether this card may change that access: the caller's own connection, managed here. The access
   * PATCH names the connection, and only its owner may change it, which is the same rule as disconnect.
   */
  const accessConnection = accessLine && canDisconnect ? connection : undefined;

  /** The card's own connect / reconnect: the connection it stands for, or a new one when it has none. */
  const handleConnect = async (): Promise<void> => {
    if (connectDisabled) return;
    await startConnect(connection?.connectionId ?? null);
  };

  /** "Add another": a second connection of this provider, always named before it leaves. */
  const handleConnectAnother = async (): Promise<void> => {
    if (connectDisabled) return;
    await startConnect(null);
  };

  /**
   * Starts the connect another surface asked for, once.
   *
   * The guard is a ref rather than the effect's dependencies: `startConnect` is rebuilt every render,
   * and listing it would fire a second connect the moment anything in this card changed — which a
   * connect does, immediately, by opening a dialog.
   *
   * It runs only where the card would have offered the press itself: an editor+, and either a card
   * with nothing connected or one that carries "add another". Anywhere else the request is one this
   * card could not have granted, and it is ignored rather than routed around the rule.
   */
  const connectOnOpenFiredRef = useRef(false);
  useEffect(() => {
    if (!connectOnOpen || connectOnOpenFiredRef.current || connectDisabled)
      return;
    if (!canManage) return;
    if (!offersAnother && connected) return;
    connectOnOpenFiredRef.current = true;
    void startConnect(connected ? null : (connection?.connectionId ?? null));
  }, [connectOnOpen, canManage, offersAnother, connected, connectDisabled]);

  // Open the in-DOM confirmation modal. Teams sandboxes the iframe and
  // suppresses `window.confirm` (returns false without showing the
  // dialog), so we use the project's AppDialog component everywhere.
  // Project rule: never use window.confirm / alert / prompt — see CLAUDE.md.
  const openConfirm = (): void => {
    if (disconnecting) return;
    setConfirmOpen(true);
  };

  const handleDisconnect = async (): Promise<void> => {
    if (!connection) return;
    setConfirmOpen(false);
    setDisconnecting(true);
    try {
      await api.disconnectProvider(
        meta.id,
        connection.connectionId,
        workspaceId,
      );
      invalidate();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to disconnect';
      notify.error(message);
    } finally {
      setDisconnecting(false);
    }
  };

  /**
   * Writes what the settings dialog changed onto this connection. An empty patch is not sent at all —
   * there is nothing to say to the server, and a body of `{}` would still cost a round trip and a
   * refetch. The dialog closes on the server's answer, never on the click.
   */
  const handleSettingsSave = async (
    patch: ConnectionProfilePatch,
  ): Promise<void> => {
    if (!connection) return;
    if (Object.keys(patch).length === 0) {
      setSettingsOpen(false);
      return;
    }
    setSavingSettings(true);
    try {
      await api.updateConnectionProfile(
        meta.id,
        connection.connectionId,
        patch,
        workspaceId,
      );
      invalidate();
      setSettingsOpen(false);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Could not save this connection';
      notify.error(message);
    } finally {
      setSavingSettings(false);
    }
  };

  const connectorHintRef = useHintTarget<HTMLElement>(
    `connector.${meta.id}`,
    'gate',
  );

  return (
    <article
      ref={connectorHintRef}
      className={cn(
        'group relative flex min-h-[8.5rem] flex-col gap-2 rounded-xl border border-border/60 bg-card/40 p-4 transition-all',
        'hover:border-border hover:bg-card hover:shadow-sm',
      )}
      aria-label={
        connection
          ? `${meta.name} connector: ${connection.label}`
          : `${meta.name} connector`
      }
    >
      {/* sr-only state text for the default ("not connected") case. When
          connected, the pill on the right carries the same word visually. */}
      {!connected && <span className="sr-only">Not connected</span>}

      {/*
        Header: icon, then the title block. The CONNECTION leads and the provider sits under it,
        because with two connections of one provider on screen the provider name is the half both
        cards share — it tells them apart of nothing, while the connection's own name is the only
        thing that does. A card with no connection has nothing to lead with, so there the provider
        keeps the title and its vendor sits below, exactly as before.

        Identity only; every control sits at the foot of the card. The pill there is the single
        source of connection state — it reads "Connected" and morphs to "Disconnect" on hover where
        removing is allowed, so disconnect lives in the same place as connect. Read-only viewers see
        a static presence pill.
      */}
      <div className="relative z-10 flex items-start gap-3">
        <ConnectorIcon id={meta.id} className="size-10 shrink-0" />
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 text-sm font-semibold leading-tight text-foreground">
            {connection ? connection.label : meta.name}
          </h3>
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
            {connection ? meta.name : meta.vendor}
          </p>
        </div>
      </div>

      {/* Body: what this connector is, and what can be done with it. */}
      <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
        {meta.description}
      </p>
      {connection?.usageHint && (
        <p className="line-clamp-3 text-caption leading-relaxed text-foreground/80">
          {connection.usageHint}
        </p>
      )}
      {!connected && (
        <p className="text-caption leading-relaxed text-muted-foreground">
          You connect {meta.name} under your own account, and only you use it.
          Material your colleagues already added to the workspace knowledge base
          stays searchable without a connection of your own.
        </p>
      )}
      {accessLine && (
        <div className="flex flex-col gap-1.5 pt-1">
          <p className="text-xs leading-relaxed text-muted-foreground">
            {accessLine}
          </p>
          {accessConnection && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() =>
                  flow.openAccessEdit(accessConnection.connectionId)
                }
                disabled={savingAccess}
                className={cn(cardButtonClass, 'whitespace-nowrap')}
              >
                {savingAccess ? (
                  <>
                    <Loader2Icon
                      className="size-3 animate-spin"
                      aria-hidden="true"
                    />
                    <span>Saving…</span>
                  </>
                ) : (
                  'Change access'
                )}
              </button>
            </div>
          )}
        </div>
      )}
      {needsReconnect && (
        <div className="flex flex-col gap-1.5 pt-1">
          <p className="text-caption leading-relaxed text-tone-warning">
            {inheritedFromPersonal
              ? 'Sign-in expired or was blocked. Reconnect it in your personal space to keep using it.'
              : 'Sign-in expired or was blocked. Reconnect to keep using it.'}
          </p>
          {!inheritedFromPersonal && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => void handleConnect()}
                disabled={oauth.busy || connectDisabled}
                className={cn(cardButtonClass, 'whitespace-nowrap')}
              >
                {oauth.busy ? (
                  <>
                    <Loader2Icon
                      className="size-3 animate-spin"
                      aria-hidden="true"
                    />
                    <span>Waiting…</span>
                  </>
                ) : (
                  'Reconnect'
                )}
              </button>
            </div>
          )}
        </div>
      )}
      {meta.id === 'powerbi' && oauth.error && oauth.error.status !== 409 && (
        <p role="alert" className="text-caption text-destructive">
          {oauth.error.message}
        </p>
      )}
      {/*
        Everything actionable lives here, at the foot of the card, and the header carries nothing but
        identity. The status pill used to sit in the header row beside the title, and since neither the
        icon nor the pill sheds width, the whole deficit landed on the provider's name — in the chat
        modal's narrow column it read "Conflue…" and "Salesforce (…". The pill says one thing only,
        whether this connector is connected; where the connection lives is the header's chip.
      */}
      <div className="mt-auto flex flex-col items-start gap-2 pt-2">
        {/*
          The state and its settings sit on ONE row: the pill says whether this connector is
          connected, and the gear beside it opens what can be said about the connection. They belong
          together because both are about the connection this card stands for, and a settings button
          on its own line read as a third action rather than an attribute of the state above it.
        */}
        {/*
          The state and its settings are ONE row that cannot break: the gear is `shrink-0` and the pill
          may shrink, so a long state ("Connected as personal") can never push the gear onto a line of
          its own — which is exactly what it did while this row was allowed to wrap.
        */}
        <div className="flex items-center gap-2">
          {/*
            No settings in the caller's own personal space. There is nothing to tell apart there — a
            person holds one connection per provider — so the name and the instruction are asked for
            and edited in the working workspace where a second one actually appears. The base values
            behind them are therefore not editable from the interface at all: a personal connection
            keeps its computed name, and describing it is done per workspace. That is a deliberate
            trade, decided 2026-08-24: fewer controls here, the same sentence typed in each workspace
            that needs it.

            It leads the row rather than following the pill: the pill's width moves with its own text
            ("Connected" against "Connected as personal"), and a control that follows a moving element
            moves with it. Anchored on the left it is in the same place on every card in the grid.
          */}
          {connection && sharing !== 'personal' && (
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              aria-label={`Settings for ${connection.label}`}
              title="Name and instruction"
              className={cn(cardButtonClass, 'size-7 px-0')}
            >
              <SettingsIcon className="size-3.5" aria-hidden="true" />
            </button>
          )}
          {inheritedFromPersonal ? (
            /*
              The pill carries the hint as its own title: it is the element that now says where
              this connection lives, so the sentences behind those two words belong on it and
              nowhere else.
            */
            <span
              className={cn(
                'inline-flex min-h-7 min-w-0 items-center justify-center gap-1 rounded-full py-1 text-left px-3 text-xs font-medium',
                pillTone,
              )}
              title={originHint ?? undefined}
            >
              <PillIcon
                className="size-3"
                strokeWidth={2.5}
                aria-hidden="true"
              />
              <span className="truncate">{pillLabel}</span>
            </span>
          ) : connected ? (
            canDisconnect && connection ? (
              <button
                type="button"
                title={originHint ?? undefined}
                onClick={openConfirm}
                disabled={disconnecting}
                aria-label={
                  disconnecting
                    ? 'Disconnecting'
                    : `Disconnect ${disconnectTarget}`
                }
                className={cn(
                  'group/pill inline-flex min-h-7 min-w-0 items-center justify-center gap-1 rounded-full py-1 text-left px-3 text-xs font-medium transition-colors hover:bg-red-500/10 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:text-red-400',
                  pillTone,
                )}
              >
                {disconnecting ? (
                  <>
                    <Loader2Icon
                      className="size-3 animate-spin"
                      aria-hidden="true"
                    />
                    <span>Removing…</span>
                  </>
                ) : (
                  <>
                    <span className="inline-flex items-center gap-1 group-hover/pill:hidden">
                      <PillIcon
                        className="size-3"
                        strokeWidth={2.5}
                        aria-hidden="true"
                      />
                      <span className="truncate">{pillLabel}</span>
                    </span>
                    <span className="hidden items-center gap-1 group-hover/pill:inline-flex">
                      <XIcon
                        className="size-3"
                        strokeWidth={2.5}
                        aria-hidden="true"
                      />
                      Disconnect
                    </span>
                  </>
                )}
              </button>
            ) : (
              <span
                className={cn(
                  'inline-flex min-h-7 min-w-0 items-center justify-center gap-1 rounded-full py-1 text-left px-3 text-xs font-medium',
                  pillTone,
                )}
                title={originHint ?? undefined}
              >
                <PillIcon
                  className="size-3"
                  strokeWidth={2.5}
                  aria-hidden="true"
                />
                <span className="truncate">{pillLabel}</span>
              </span>
            )
          ) : (
            <button
              type="button"
              onClick={() => void handleConnect()}
              disabled={oauth.busy || connectDisabled}
              className={cardButtonClass}
            >
              {oauth.busy ? (
                <>
                  <Loader2Icon
                    className="size-3 animate-spin"
                    aria-hidden="true"
                  />
                  <span>Waiting…</span>
                </>
              ) : (
                'Connect'
              )}
            </button>
          )}
        </div>
        {selectsFolders && connection && workspaceId && (
          <SelectFoldersButton
            workspaceId={workspaceId}
            connectionId={connection.connectionId}
            onOpen={() => setFoldersOpen(true)}
          />
        )}
        {offersAnother && (
          <button
            type="button"
            onClick={() => void handleConnectAnother()}
            disabled={oauth.busy || connectDisabled}
            className={cn(cardButtonClass, 'whitespace-nowrap')}
          >
            {oauth.busy ? (
              <>
                <Loader2Icon
                  className="size-3 animate-spin"
                  aria-hidden="true"
                />
                <span>Waiting…</span>
              </>
            ) : (
              `Connect another ${meta.name}`
            )}
          </button>
        )}
      </div>

      <AppDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`Disconnect ${disconnectTarget}?`}
        titleHidden
        showCloseButton={false}
        className="flex w-full max-w-[var(--dialog-w-sm)] flex-col gap-0 overflow-hidden rounded-xl border-border/50 p-0 shadow-xl"
      >
        <div className="px-6 pt-6 pb-2">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold tracking-tight text-foreground">
              Disconnect {disconnectTarget}?
            </h3>
            <DialogClose asChild>
              <button
                type="button"
                aria-label="Close"
                className="-mt-1 -mr-1 flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
              >
                <XIcon className="size-4" />
              </button>
            </DialogClose>
          </div>
          <p className="mt-2 text-compact leading-relaxed text-muted-foreground">
            You&apos;ll need to reconnect to use {meta.name} in chat. Stored
            credentials will be removed.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 pb-5 pt-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="destructive-outline"
            size="sm"
            onClick={() => void handleDisconnect()}
          >
            Disconnect
          </Button>
        </div>
      </AppDialog>

      {/*
        Mounted only while it is open. Closed, it is still a component with queries of its own, and
        a directory renders a card per connection — so leaving it mounted cost several reads per
        card for a dialog nobody had asked for yet.
      */}
      {foldersOpen && connection && workspaceId && (
        <SelectFoldersDialog
          open
          onOpenChange={setFoldersOpen}
          workspaceId={workspaceId}
          connectionId={connection.connectionId}
          connectionLabel={connection.label}
          /**
           * Closed first, because the naming form is a dialog too and the two would otherwise
           * stack. The person is choosing a connection now, not folders of the old one.
           */
          onConnectAnother={() => {
            setFoldersOpen(false);
            void handleConnectAnother();
          }}
        />
      )}

      {connection && (
        <ConnectionSettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          providerName={meta.name}
          connection={connection}
          personalWorkspace={sharing === 'personal'}
          qualitySharingWorkspaceId={
            sharing === 'shared' && meta.id !== 'powerbi' ? workspaceId : null
          }
          busy={savingSettings}
          onSave={handleSettingsSave}
        />
      )}

      {flow.dialogs}
    </article>
  );
}
